/**
 * Pure deep-link parsing/routing, extracted from App.vue.
 *
 * These functions are side-effect free (no store, no `this`, no router) so they can be
 * unit-tested and reasoned about in isolation. App.vue is responsible for the side effects
 * (store mutations, analytics, navigation); it calls these to compute the *what*.
 */

export interface DeepLinkParameters {
  utm_id?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  webGoogleClickId?: string;
  webFacebookClickId?: string;
  webGoogleClientId?: string;
  webFacebookId?: string;
}

// Our internal parameter name -> the query-string key it comes from.
const PARAMETER_SOURCE: Record<keyof DeepLinkParameters, string> = {
  utm_id: 'utm_id',
  utm_source: 'utm_source',
  utm_medium: 'utm_medium',
  utm_campaign: 'utm_campaign',
  utm_term: 'utm_term',
  utm_content: 'utm_content',
  webGoogleClickId: 'gclid',
  webFacebookClickId: 'fbclid',
  webGoogleClientId: 'googleClientId',
  webFacebookId: 'facebookClientId'
};

/**
 * Extract the marketing/attribution parameters present on a deep link.
 * Returns only the keys that are actually present (mirrors the previous `...(x && { x })` spread).
 */
export function extractDeepLinkParameters(url: string): DeepLinkParameters {
  const params = new URL(url).searchParams;
  const result: DeepLinkParameters = {};
  (Object.keys(PARAMETER_SOURCE) as (keyof DeepLinkParameters)[]).forEach((key) => {
    const value = params.get(PARAMETER_SOURCE[key]);
    if (value) result[key] = value;
  });
  return result;
}

// Single-segment paths that map straight through to `/{platform}/{path}`.
const STRAIGHT_FORWARD_URLS = [
  'marketplace',
  'investments',
  'account',
  'support',
  'register',
  'login',
  'settings',
  'reset-password',
  'identification',
  'security-app-integration',
  'imprint-details'
];

/**
 * Resolve an incoming deep-link URL to the in-app route it should forward to.
 * Handles the branded-app URL schemas, verification/password tokens, and the known path prefixes.
 * Falls back to the marketplace for anything unrecognized (no open redirect).
 *
 * @param url the raw deep-link URL
 * @param appPlatform the active platform segment ('app' | 'mobileWeb' | 'desktopWeb')
 */
export function resolveDeepLinkRoute(url: string, appPlatform: string): string {
  const cleanUrl = url.split('?')[0];
  const site = cleanUrl
    // Remove the branded app url schema
    .replace(/^(\w+\.)?brickwise\.app\//, '')
    .replace(/^(?:[^.]+\.)*bwplatform\.app\//, '')
    // Remove the branded app local url schema
    .replace(/^[^.]+\.(brickwise):\/\//, '')
    .replace(/^[^.]+\.(bwplatform):\/\//, '')
    // Remove the old general schema
    .replace('brickwise://', '')
    .replace('https://', '')
    .replace('http://', '')
    .replace('app.brickwise.at/', '')
    .replace('staging.', '')
    .replace('app/', '')
    .replace('desktopWeb/', '')
    .replace('mobileWeb/', '')
    // for local testing of verification links
    .replace('localhost.brickwise.at:8080/', '');
  const subSiteArray = site.split('/');

  // handle registerSpecialFlow and introSlides flow / verification links
  if (subSiteArray.length === 2 && (subSiteArray[0] === 'register' || subSiteArray[0] === 'verify')) {
    return `/${appPlatform}/register?verificationToken=${subSiteArray[1]}`;
  } else if (subSiteArray.length === 2 && subSiteArray[0] === 'password-reset') {
    return `/${appPlatform}/reset-password?passwordToken=${subSiteArray[1]}`;
  }

  if (subSiteArray.length === 1) {
    if (STRAIGHT_FORWARD_URLS.includes(site)) {
      return `/${appPlatform}/${site}`;
    } else if (subSiteArray[0] === 'imprint') {
      return appPlatform === 'app'
        ? `/${appPlatform}/settings?option=imprintDetails`
        : `/${appPlatform}/imprint-details`;
    }
    return `/${appPlatform}/marketplace`;
  }

  if (subSiteArray[0] === 'forum') {
    return `/${appPlatform}/asset/${subSiteArray[1]}`;
  } else if (subSiteArray[0] === 'transaction') {
    return `/${appPlatform}/account?transaction=${subSiteArray[1]}`;
  } else if (subSiteArray[0] === 'assets' || subSiteArray[0] === 'asset') {
    return subSiteArray.length === 2
      ? `/${appPlatform}/asset/${subSiteArray[1]}`
      : `/${appPlatform}/asset/${subSiteArray[1]}/${subSiteArray[2]}`;
  } else if (subSiteArray[0] === 'settings') {
    return `/${appPlatform}/settings?option=${subSiteArray[1]}`;
  }
  return `/${appPlatform}/marketplace`;
}
